/**
 * The provider portal's read surface (EP-004P Phase 1).
 *
 * Every type here mirrors what `emergeDaySheet.js` actually returns. Where a
 * column is nullable in the database it is nullable here, because the screens
 * have to say "not recorded" rather than render an empty cell that reads as a
 * value of zero or an empty string.
 *
 * There is deliberately no write method. Every field a provider would edit —
 * scheduling status, delivery date, visit completion — is MONDAY-owned under
 * EP-004A, and the payload says so in `provenance.writable`. A write method
 * here would be a promise the backend refuses to keep.
 */

import { api } from '../../services/api';

export interface ProviderFacility {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  region: string | null;
}

/**
 * `enterprise` means the caller's capabilities are not facility-constrained.
 * They get a bounded, searchable window onto the estate rather than a
 * switcher: `facilities` is the current page, `total` how many exist, and
 * `searchable` says the server will filter on `q`. A facility-constrained
 * caller gets their own buildings and no search.
 */
export interface FacilityScope {
  enterprise: boolean;
  facilities: ProviderFacility[];
  total?: number;
  searchable?: boolean;
  query?: string;
  /**
   * Shared external workbooks the provider works in alongside this portal.
   * Configured per deployment, not per facility; null when not configured,
   * so the shell shows nothing rather than a dead link.
   */
  links?: { stock_report: string | null };
}

export interface DaySheetFacility {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  county: string | null;
  region: string | null;
  phone: string | null;
  corporation: string | null;
  /** The board's Licensed Beds (text). */
  licensed_beds: string | null;
  /** The board's "# of Beds" — a different field; shown under its own name, never merged. */
  beds: number | string | null;
  active_clients: string | null;
  /** Derived: confirmed residents here whose PA lead is ENROLLED. Not the board's hand-typed Active Clients. */
  enrolled_residents: number | null;
  status: string | null;
  credentialing_status: string | null;
}

export interface FacilityContact { id: string; name: string | null; title: string | null; phone: string | null; email: string | null }
export interface CheckInRecord {
  id: string; checked_in_at: string; contact_id: string | null; contact_name_snapshot: string | null; contact_role_snapshot: string | null;
}

/**
 * Testing status — should the provider screen this resident, or does
 * someone need to verify them first? One canonical value derived on the
 * server from the structured qualification field and an approved whitelist
 * of operational words in the tag columns. `conflict` means an eligible and
 * an ineligible word are both on the record (Dagmar): verify, do not guess.
 */
export type TestingStatus =
  | 'QUALIFIED' | 'QUALIFIED_2ND_TIER' | 'NEEDS_VERIFICATION' | 'UNABLE_TO_ACCESS' | 'MEDICAID_PENDING' | 'NEED_SSN'
  | 'NO_MEDICAID' | 'LOST_MEDICAID' | 'WRONG_TYPE_MEDICAID' | 'DNQ' | 'DNQ_PAYER_ISSUE' | 'DNQ_PL_TOO_LOW' | 'PRIVATE_PAY';
export const TESTING_STATUS_LABEL: Record<TestingStatus, string> = {
  QUALIFIED: 'Qualified', QUALIFIED_2ND_TIER: 'Qualified · 2nd tier', NEEDS_VERIFICATION: 'Needs verification',
  UNABLE_TO_ACCESS: 'Unable to access', MEDICAID_PENDING: 'Medicaid pending', NEED_SSN: 'Need SS #',
  NO_MEDICAID: 'No Medicaid', LOST_MEDICAID: 'Lost Medicaid', WRONG_TYPE_MEDICAID: 'Wrong type Medicaid',
  DNQ: 'DNQ', DNQ_PAYER_ISSUE: 'DNQ · payer issue', DNQ_PL_TOO_LOW: 'DNQ · PL too low', PRIVATE_PAY: 'Private pay',
};
/** The hover text for a testing-status badge: where it came from, and whether Monday disagrees. */
export function testingTitle(p: {
  testing_status: TestingStatus | null; testing_status_values: TestingStatus[]; testing_status_conflict: boolean;
  testing_status_source: RosterPatient['testing_status_source']; testing_status_monday: TestingStatus | null; testing_status_drift: boolean;
}): string | undefined {
  const parts: string[] = [];
  if (p.testing_status_conflict) parts.push(`Conflicting on the record: ${p.testing_status_values.map((v) => TESTING_STATUS_LABEL[v]).join(' and ')}`);
  if (p.testing_status_source === 'COUNSELEAR') {
    parts.push('From CounselEAR');
    if (p.testing_status_drift) parts.push(`Monday says ${p.testing_status_monday ? TESTING_STATUS_LABEL[p.testing_status_monday] : 'nothing'}`);
  } else if (p.testing_status_source === 'TAGS') {
    parts.push('Read from the tag column');
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** Test / verify first / do not test — the three things a provider needs to know at a glance. */
export function testingTone(s: TestingStatus | null): 'ok' | 'attn' | 'stop' | '' {
  if (!s) return '';
  if (s === 'QUALIFIED' || s === 'QUALIFIED_2ND_TIER') return 'ok';
  if (s === 'NEEDS_VERIFICATION' || s === 'MEDICAID_PENDING' || s === 'NEED_SSN') return 'attn';
  return 'stop';
}

export type ResidentStatus = 'Active' | 'Prospect' | 'Inactive' | 'Deceased';
export const RESIDENT_STATUSES: readonly ResidentStatus[] = ['Active', 'Prospect', 'Inactive', 'Deceased'];
export type RosterOrder = 'name' | 'room';

/**
 * The manifest's lenses (April Torino, 2026-09-09): Census · Active ·
 * Enrolled as chips, the rest under More. The server owns every predicate
 * (`daySheetService.ROSTER_LENSES`) and returns every count; this is what
 * the screen calls them and what each means to a provider. Census and
 * Active are the same rows today, by April's own definitions, and stay two
 * lenses so they can diverge without a screen change. ATD is the board's ATD
 * column saying Yes — approved to dispense; Dispensed means a current
 * hearing-aid wearer and is not on the tab (April, 2026-09-10). Enrolled and
 * ATD leave Inactive and Deceased to More, where they stay reachable.
 */
export type RosterLens = 'manifest' | 'census' | 'active' | 'enrolled' | 'atd' | 'inactive' | 'deceased' | 'no-status';
export const DEFAULT_LENS: RosterLens = 'manifest';
export const ROSTER_LENSES: ReadonlyArray<{ key: RosterLens; label: string; primary: boolean; what: string }> = [
  { key: 'manifest', label: 'Current manifest', primary: true, what: 'Who to consider on this visit: the census without residents screened DNQ whose annual is not yet due. New referrals are here.' },
  { key: 'census', label: 'Census', primary: true, what: 'Everyone we presently understand to be at this facility: Active or Prospect.' },
  { key: 'active', label: 'Active', primary: false, what: 'Active or Prospect. Prospect is a new opportunity or new referral.' },
  { key: 'enrolled', label: 'Enrolled', primary: true, what: 'Residents whose Patient Advocate lead is enrolled, not inactive or deceased.' },
  { key: 'atd', label: 'ATD', primary: true, what: 'Approved to dispense: the ATD column says Yes. Residents already dispensed are current hearing-aid wearers and are not here.' },
  { key: 'inactive', label: 'Inactive', primary: false, what: 'Resident status Inactive.' },
  { key: 'deceased', label: 'Deceased', primary: false, what: 'Resident status Deceased.' },
  { key: 'no-status', label: 'No status recorded', primary: false, what: 'A confirmed placement with no resident status on the board.' },
];
export const isLens = (v: string | null | undefined): v is RosterLens => ROSTER_LENSES.some((l) => l.key === v);
export const lensLabel = (k: RosterLens): string => ROSTER_LENSES.find((l) => l.key === k)?.label || k;

/**
 * A list, not an object — one row per facility is expected but nothing in the
 * schema enforces it, and the service returns all of them rather than picking
 * one silently.
 */
export interface SchedulingRow {
  status: string | null;
  date_of_last_visit: string | null;
  provider_next_visit_at: string | null;
  tentative_next_visit: string | null;
  manifest_uploaded: string | null;
  census_date: string | null;
  tm_follow_up_visit_date: string | null;
  comments: string | null;
  /**
   * Link to the facility's SharePoint charting workbook — the document
   * providers actually work in. May be null: not every facility has one
   * recorded, and that absence is itself worth showing rather than hiding.
   */
  cloud_manifest_url: string | null;
}

export interface PendingFit {
  id: string;
  serial_number: string | null;
  ear: string | null;
  hearing_aid_type: string | null;
  status: string | null;
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
}

/**
 * One resident, many devices (April Torino, 2026-09-11). The disposition is
 * derived from the device rows and the board's replacement words — ATD or
 * REPLACEMENT, and which ears — never a tag copied through. Device rows stay
 * underneath; a serial that does not exist yet is "not yet assigned", not
 * missing, because serials are only written after dispense.
 */
export interface FitResident {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  disposition: { kind: 'ATD' | 'REPLACEMENT'; ears: Array<'LEFT' | 'RIGHT'> };
  devices: Array<Pick<PendingFit, 'id' | 'serial_number' | 'ear' | 'hearing_aid_type' | 'status'>>;
}
/** A device the board still shows undelivered that an authoritative source records as dispensed. Ids only. */
export interface DispensedElsewhere { id: string; patient_id: string; dispensed_by: Array<'COUNSELEAR' | 'PROVIDER_RECORD'> }

export function dispositionLine(d: FitResident['disposition']): string {
  const ears = d.ears.length === 2 ? 'BOTH' : d.ears[0] || null;
  return [d.kind === 'REPLACEMENT' ? 'ATD — REPLACEMENT' : 'ATD', ears].filter(Boolean).join(' — ');
}

/** Month and year only: the business ball-parks the annual; a day would be invented precision. */
export function monthYear(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export interface BlockedUmed {
  id: string;
  faxed_status: string | null;
  status: string | null;
  submitted_date: string | null;
  last_follow_up_date: string | null;
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
}

/**
 * `counselear_id` is absent — not null — for anyone the caller is not care
 * assigned to, which is why it is optional here. `care_assigned` states the
 * reason so a screen never has to infer it from the missing field.
 */
export interface RosterPatient {
  id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  status: string | null;
  phase: string | null;

  /**
   * A text[] in the database, not a scalar — and it genuinely carries more
   * than one value: 26 residents are both MEDICAID PENDING and QUALIFIED.
   * The manifest spreadsheet has a single STATUS cell and cannot express
   * that, so it records whichever the author picked. Rendering the array is
   * how the platform stops losing the second value.
   */
  qualification_status: string[] | null;
  priority: string | null;
  /** The board's own word: Man, Woman, Nonbinary/Other. Not translated. */
  gender: string | null;
  testing_status: TestingStatus | null;
  testing_status_values: TestingStatus[];
  testing_status_conflict: boolean;
  /**
   * COUNSELEAR once the resident's CounselEAR record has been read: the
   * portal is 1:1 with CounselEAR, and Monday's reading is then carried in
   * `testing_status_monday`, with `testing_status_drift` when they differ.
   * The Monday-derived sources remain for residents not yet read.
   */
  testing_status_source: 'COUNSELEAR' | 'QUALIFICATION' | 'TAGS' | 'BOTH' | null;
  testing_status_monday: TestingStatus | null;
  testing_status_drift: boolean;
  testing_status_synced_at: string | null;
  /** The board's annual-screening flag (Annual Appointment Status = Due). */
  annual_due: boolean;
  annual_appt_date: string | null;
  /** Derived: this resident's PA lead is ENROLLED. */
  enrolled: boolean;
  /**
   * Census versus current manifest (April, 2026-09-11). A resident in the
   * census but off the manifest says why; today the one reason is a DNQ
   * screening whose annual is not yet due. New referral is what Prospect
   * means to the business.
   */
  on_manifest?: boolean;
  manifest_exclusion?: 'SCREENED_DNQ' | null;
  new_referral?: boolean;
  /** The board's DNQ date: when the resident was screened and did not qualify. */
  dnq_date?: string | null;

  /**
   * Office-side workflow marks. Released with the census.
   */
  office_tags?: string[] | null;

  /*
   * Everything below is released ONLY to a caller care-assigned to this
   * patient, and is ABSENT — not null — otherwise. `care_assigned` states the
   * reason, so a screen never has to infer it from a missing field.
   *
   * `tags` looks like it belongs with office_tags and does not: the real
   * manifests carry "Screened-NO HT", "Current Hearing Aids" and "Debrox", a
   * medication, in that column.
   */
  counselear_id?: string | null;
  tags?: string[] | null;
  provider_tags?: string[] | null;
  alerts?: string | null;
  notes?: string | null;
  care_assigned: boolean;
}

/** P1-D (2026-09-21): the visit plan Scheduling keeps in eMerge. Null fields mean no plan yet, not a missed visit. */
export interface VisitPlan {
  source: 'EMERGE';
  next: { id: string; planned_on: string; state: 'PLANNED' | 'CONFIRMED'; purpose: string | null; provider_name: string | null } | null;
  last: { id: string; completed_on: string | null; provider_name: string | null } | null;
}

/**
 * The building's manifest link (MAN-4, 2026-10-05). `current` is the one eMerge
 * holds and the one to open; `mondayReference` is Monday's column, shown only
 * as a labelled reference. A Monday sync never changes `current`.
 */
export interface DaySheetManifestLink {
  current: { url: string; source: 'EMERGE'; origin: 'EMERGE' | 'MONDAY_IMPORT'; set_at: string | null; version: number } | null;
  state: 'EMERGE' | 'CLEARED' | 'MONDAY_REFERENCE_ONLY' | 'NONE';
  mondayReference: { url: string; source: 'MONDAY'; reference: true } | null;
}

/**
 * The link a provider should open, and whether it is only Monday's reference.
 * eMerge's link first; a building eMerge holds as "no link" has none, whatever
 * Monday says. A payload from before the deploy (no `manifestLink`) falls back
 * to the scheduling row's column, which is Monday's, so it is labelled so.
 */
export function manifestLinkOf(sheet: Pick<DaySheet, 'manifestLink' | 'scheduling'>): { url: string; reference: boolean } | null {
  const ml = sheet.manifestLink;
  if (ml) {
    if (ml.current && ml.current.url) return { url: ml.current.url, reference: false };
    if (ml.state === 'CLEARED') return null;
    return ml.mondayReference ? { url: ml.mondayReference.url, reference: true } : null;
  }
  const monday = (sheet.scheduling || []).find((s) => s.cloud_manifest_url)?.cloud_manifest_url || null;
  return monday ? { url: monday, reference: true } : null;
}

export interface DaySheet {
  facility: DaySheetFacility;
  scheduling: SchedulingRow[];
  /** Optional so a payload from before the deploy still renders. */
  manifestLink?: DaySheetManifestLink | null;
  /** Optional so a payload from before the deploy still renders. */
  visitPlan?: VisitPlan | null;
  /** Devices genuinely waiting: undelivered on the board and not recorded dispensed by CounselEAR or the provider. */
  fits: PendingFit[];
  /** The same devices, one entry per resident, with the derived disposition. Optional so a payload from before the deploy still renders. */
  fitResidents?: FitResident[];
  dispensedElsewhere?: DispensedElsewhere[];
  blockedUmed: BlockedUmed[];
  contacts: { ssd: FacilityContact[]; all: FacilityContact[] };
  checkIn: { today: CheckInRecord[] };
  roster: {
    /** The selected lens's count for the whole building. */
    total: number;
    limit: number;
    offset: number;
    returned: number;
    lens: RosterLens;
    order: RosterOrder;
    /** Every lens's count, from the same predicates that selected the rows. */
    lenses: Record<RosterLens, number>;
    /** Everyone with a confirmed placement here, whatever their status. */
    confirmed: number;
    rows: RosterPatient[];
  };
  counts: {
    pendingFits: number;
    pendingFitResidents?: number;
    /** Undelivered on the board, recorded dispensed by an authoritative source. Said, never averaged. */
    dispensedElsewhere?: number;
    blockedUmed: number;
    careAssignedOnPage: number;
    annualDueOnPage: number;
    offManifestOnPage?: number;
  };
  provenance: {
    rowAuthority: string;
    chartLinkPolicy: string;
    /** The caller may edit at least one manifest column here (clinical edit rights + eMerge-owned column). */
    writable: boolean;
    /** Exactly the columns the server would accept on a manifest write; absent on a payload from before the deploy. */
    writableFields?: ManifestField[];
    writeBlockedBy: string;
  };
}

/** The manifest columns a provider may own. Which of them are open is the server's word (`provenance.writableFields`). */
export type ManifestField = 'tags' | 'provider_tags' | 'alerts' | 'notes' | 'qualification_status';
export type ManifestPatch = Partial<{ notes: string | null; alerts: string | null; provider_tags: string[] | null; tags: string[] | null }>;

interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  /** Present on a refusal: the authorization decision that produced it. */
  code?: string;
  message?: string;
  /** The HTTP status; absent with no code means the server was never reached (services/api contract). */
  status?: number;
}

/**
 * `api.get` is `apiRequest` bound to a verb, and binding loses the generic —
 * so every call casts its own result. Same treatment as `emergeApi.ts`.
 */
export const providerApi = {
  /**
   * Which buildings this provider may open. `q` searches, and only an
   * enterprise-scoped caller is offered it — a provider with six buildings
   * does not need to search six buildings.
   */
  getScope: async (q?: string): Promise<ApiResult<FacilityScope>> =>
    (await api.get(`/emerge/day-sheet${q ? `?q=${encodeURIComponent(q)}` : ''}`)) as ApiResult<FacilityScope>,

  /** Record that the caller has arrived, with whom. An event, not a checkbox. */
  checkIn: async (facilityId: string, contactId: string | null): Promise<ApiResult<CheckInRecord>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/check-in`, { contact_id: contactId })) as ApiResult<CheckInRecord>,

  /**
   * Change the manifest columns the provider owns for one care-assigned
   * resident (workforce cutover, 2026-09-19). Only the fields the server named
   * in `provenance.writableFields` are sent; the server refuses the rest.
   */
  updateManifest: async (facilityId: string, patientId: string, patch: ManifestPatch): Promise<ApiResult<{ id: string; updated: string[] }>> =>
    (await api.patch(`/emerge/day-sheet/${facilityId}/patients/${patientId}`, patch)) as ApiResult<{ id: string; updated: string[] }>,

  getDaySheet: async (
    facilityId: string,
    params: { limit?: number; offset?: number; lens?: RosterLens | null; order?: RosterOrder } = {}
  ): Promise<ApiResult<DaySheet>> => {
    const qp = new URLSearchParams();
    if (params.lens && params.lens !== DEFAULT_LENS) qp.set('lens', params.lens);
    if (params.order && params.order !== 'name') qp.set('order', params.order);
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.offset) qp.set('offset', String(params.offset));
    const qs = qp.toString();
    return (await api.get(
      `/emerge/day-sheet/${facilityId}${qs ? `?${qs}` : ''}`
    )) as ApiResult<DaySheet>;
  },
};

/** A patient's display name, without inventing one when both parts are absent. */
export function patientName(p: {
  first_name: string | null;
  last_name: string | null;
}): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return name || 'Name not recorded';
}

/**
 * Every date this portal shows is a Monday calendar day.
 *
 * The scheduling board stores them as `date` columns (five of them) or as a
 * timestamptz at midnight UTC (`provider_next_visit_at`, all 258 rows), and
 * the API hands them over as "2026-09-08T00:00:00.000Z". Read as a local
 * instant in Eastern time that is 8 PM on September 7 — which is how the
 * Schedule tab showed a September 8 visit a day early, the same defect the
 * coverage board shipped with (#87) and Today fixed for itself (#94).
 *
 * So the calendar day is the UTC date, taken here once for every caller.
 * A date-only string ("2026-09-08") parses as UTC midnight too, so it gets
 * the same answer. Verified 2026-09-03 against every column that reaches
 * these helpers: no caller carries a true time of day.
 */
function calendarDay(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth(), d: parsed.getUTCDate() };
}

/**
 * A null renders as an em dash rather than as today's date, which is what
 * `new Date(null)` would quietly produce.
 */
export function shortDate(value: string | null | undefined): string {
  const c = calendarDay(value);
  if (!c) return '—';
  return new Date(Date.UTC(c.y, c.m, c.d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}

/**
 * Whole days from the reader's own calendar day; negative is in the past.
 * Null when there is no date.
 */
export function daysFromToday(value: string | null | undefined): number | null {
  const c = calendarDay(value);
  if (!c) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const that = new Date(c.y, c.m, c.d).getTime();
  return Math.round((that - today) / 86400000);
}

/** "in 3 days" / "yesterday" / "today" — relative, because that is the decision. */
export function relativeDay(value: string | null | undefined): string | null {
  const days = daysFromToday(value);
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
